import { useMemo } from "react";
import { Pressable, StyleSheet, TextInput, View } from "react-native";

import { fontFamily, fontSize } from "@passfirst/ui";

import { CloseIcon, SearchIcon } from "../icons";
import { type Colors, useColors } from "../theme";

interface Props {
  value: string;
  onChange: (next: string) => void;
}

// Search/filter input atop the credential picker. Leading magnifier, a
// clear affordance once there's text. Filtering itself lives in
// group-credentials.ts — this is presentation only.
export function CredentialSearch({ value, onChange }: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  return (
    <View style={styles.wrap}>
      <SearchIcon size={18} color={colors.textMuted} />
      <TextInput
        value={value}
        onChangeText={onChange}
        placeholder="Search credentials"
        placeholderTextColor={colors.textMuted}
        autoCorrect={false}
        autoCapitalize="none"
        accessibilityLabel="Search credentials"
        style={styles.input}
      />
      {value.length > 0 ? (
        <Pressable
          onPress={() => onChange("")}
          accessibilityRole="button"
          accessibilityLabel="Clear search"
          hitSlop={8}
        >
          <CloseIcon size={18} color={colors.textMuted} />
        </Pressable>
      ) : null}
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    wrap: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingHorizontal: 14,
      height: 46,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 12,
      backgroundColor: colors.surface,
    },
    input: {
      flex: 1,
      fontFamily: fontFamily.regular,
      fontSize: fontSize.ui,
      color: colors.textPrimary,
    },
  });
}
